import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

// GET
//
// REJECTS on failure — deliberately. This used to `.catch()` into `undefined`,
// which every caller then dropped on the floor via `if (response && response.data)`.
// The result was a permanently empty slot picker with no error, no toast, and
// (because the callers' effects have `[]` deps) no way to retry short of a hard
// reload: staff saw the program selected and the class list simply absent.
// That is the 2026-08-12 "office lady could not put a child into a class"
// report — one transient blip stranded her for the length of a customer queue.
// A rejection is recoverable; `undefined` is not. Callers MUST surface it and
// offer a retry — see useProgramSlotsData.
//
// `slotIds` (optional): fetch exactly these slots, ARCHIVED ones included. The
// athlete pages use it for the classes an athlete is enrolled in that the
// normal list leaves out (archived), so the enrollment still shows with
// Unenroll (PM 64f03c33). Without it the list skips archived slots, as every
// enroll picker needs.
export const getProgramSlots = (slotIds) => {
  const ids = Array.isArray(slotIds) ? slotIds.filter(Boolean) : [];
  return axios
    .get(`${BASE_URL}/programs-slot`, {
      headers: {
        "Content-Type": "application/json",
      },
      ...(ids.length ? { params: { slot_ids: ids.join(",") } } : {}),
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET impact preview for archive/delete
export const getSlotDeleteImpact = (token, slotId) => {
  return axios
    .get(`${BASE_URL}/programs-slot/${slotId}/delete-impact`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// POST archive a slot (with cascade flags)
export const archiveSlot = (token, slotId, opts = {}) => {
  return axios
    .post(
      `${BASE_URL}/programs-slot/${slotId}/archive`,
      {
        archive_enrollments: opts.archive_enrollments !== false,
        archive_attendance: opts.archive_attendance !== false,
        // The server refuses (409 active_members_present) to archive a class
        // that still has athletes unless staff confirmed it. Only ever sent
        // when the caller says so.
        ...(opts.confirm_active_members === true ? { confirm_active_members: true } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// POST restore an archived slot
export const restoreSlot = (token, slotId) => {
  return axios
    .post(`${BASE_URL}/programs-slot/${slotId}/restore`, null, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// POST move enrollments from this slot to another slot in the same program
// forceCapacityOverride: staff with the capacity-override permission confirmed
// moving past a full class (the server answers CAPACITY_FULL first).
export const moveSlotEnrollments = (token, slotId, targetSlotId, enrollmentIds, forceCapacityOverride = false) => {
  return axios
    .post(
      `${BASE_URL}/programs-slot/${slotId}/move-enrollments`,
      {
        target_slot_id: targetSlotId,
        ...(Array.isArray(enrollmentIds) && enrollmentIds.length > 0
          ? { enrollment_ids: enrollmentIds }
          : {}),
        ...(forceCapacityOverride ? { force_capacity_override: true } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// DELETE a slot (only when impact is fully clean)
export const hardDeleteSlot = (token, slotId) => {
  return axios
    .delete(`${BASE_URL}/programs-slot/${slotId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};
